/**
 * La Coochie header search row (sections/lc-header.liquid, Caroline 2026-10-12, oddobody.com reference).
 *
 * The search icon toggles a full-width row under the bar: an input and a ×. Typing fetches Shopify's predictive
 * search through the Section Rendering API (sections/lc-predictive-search.liquid) and fills two columns: products
 * (thumbnail, title, price) and "popular searches" — Shopify's query suggestions, then the matching entries of the
 * merchant's own list (header setting) — then a "see all products" link
 * to the full results page. Enter submits to /search as a normal form. Escape, the × and a press outside close it.
 *
 * Built on @theme/component; requests are debounced and aborted when a newer query starts.
 */
import { Component } from '@theme/component';

const DEBOUNCE = 200;
const SECTION_ID = 'lc-predictive-search';

/** @param {string} text */
const normalize = (text) => text.toLowerCase().replace(/[-–]/g, ' ').replace(/\s+/g, ' ').trim();
/** @param {string} text */
const words = (text) => normalize(text).split(' ').filter(Boolean);
/** Every typed word starts some word of the entry ("thon" → "high rise thong"). @param {string} entry @param {string} query */
const matches = (entry, query) => {
  const entryWords = words(entry);
  const typed = words(query);
  return typed.length > 0 && typed.every((w) => entryWords.some((e) => e.startsWith(w)));
};

class LcSearch extends Component {
  requiredRefs = ['input', 'results', 'products', 'popular', 'queries', 'empty', 'all'];

  /** @type {AbortController | null} */
  #controller = null;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #timer;
  /** @type {Map<string, Document>} */
  #cache = new Map();

  /** The merchant's own popular searches (header setting "Your popular searches"): one per line, "text" or "text | link". @type {{ text: string, link: string }[] | null} */
  #popularList = null;

  get #popular() {
    if (!this.#popularList) {
      try {
        this.#popularList = JSON.parse(this.dataset.popular || '[]')
          .map((/** @type {string} */ line) => {
            const [text = '', link = ''] = line.split('|').map((part) => part.trim());
            return { text, link };
          })
          .filter((/** @type {{ text: string }} */ entry) => entry.text);
      } catch {
        this.#popularList = [];
      }
    }
    return this.#popularList;
  }

  /**
   * A list item for one of the merchant's searches, marked up like Shopify's styled_text (typed part plain, added words
   * in <span>, shown bold), linking to the results page.
   * @param {{ text: string, link: string }} entry
   * @param {string} query
   */
  #popularItem({ text: entry, link: target }, query) {
    const typed = words(query);
    const link = document.createElement('a');
    link.className = 'lc-search__query';
    link.dataset.testid = 'lc-search-query';
    const url = new URL(this.dataset.searchUrl || '/search', location.origin);
    url.searchParams.set('q', entry);
    url.searchParams.set('options[prefix]', 'last');
    link.href = target || url.pathname + url.search;
    entry.split(/(\s+)/).forEach((part) => {
      if (!part.trim()) return link.append(part);
      const known = typed.some((w) => normalize(part).startsWith(w));
      if (known) link.append(part);
      else link.append(Object.assign(document.createElement('span'), { textContent: part }));
    });
    const li = document.createElement('li');
    li.append(link);
    return li;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#removeGlobalListeners();
    this.#controller?.abort();
  }

  get isOpen() {
    return this.hasAttribute('open');
  }

  get #triggers() {
    return document.querySelectorAll(`[aria-controls="${this.id}"]`);
  }

  toggle() {
    if (this.isOpen) this.close();
    else this.open();
  }

  open() {
    if (this.isOpen) return;
    this.setAttribute('open', '');
    for (const trigger of this.#triggers) trigger.setAttribute('aria-expanded', 'true');
    this.refs.input.focus({ preventScroll: true });
    document.addEventListener('keydown', this.#onKeyDown);
    document.addEventListener('pointerdown', this.#onPointerDown, true);
    if (this.refs.input.value.trim()) this.#search();
  }

  close() {
    if (!this.isOpen) return;
    this.removeAttribute('open');
    for (const trigger of this.#triggers) trigger.setAttribute('aria-expanded', 'false');
    this.#removeGlobalListeners();
    clearTimeout(this.#timer);
    this.#controller?.abort();
    if (this.contains(document.activeElement)) /** @type {HTMLElement | undefined} */ (this.#triggers[0])?.focus({ preventScroll: true });
  }

  onInput() {
    clearTimeout(this.#timer);
    this.#timer = setTimeout(() => this.#search(), DEBOUNCE);
  }

  #removeGlobalListeners() {
    document.removeEventListener('keydown', this.#onKeyDown);
    document.removeEventListener('pointerdown', this.#onPointerDown, true);
  }

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if (event.key === 'Escape') this.close();
  };

  /** A press anywhere outside the row (and outside the icon, which toggles by itself) closes it. @param {PointerEvent} event */
  #onPointerDown = (event) => {
    const target = /** @type {Node} */ (event.target);
    if (this.contains(target) || [...this.#triggers].some((t) => t.contains(target))) return;
    this.close();
  };

  async #search() {
    const query = this.refs.input.value.trim();
    this.#controller?.abort();
    if (!query) {
      this.refs.results.hidden = true;
      return;
    }

    const products = Number(this.dataset.products) || 4;
    const queries = Number(this.dataset.queries) || 6;
    const url = new URL(this.dataset.suggestUrl || '/search/suggest', location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'product,query');
    url.searchParams.set('resources[limit]', String(Math.max(products, queries)));
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][prefix]', 'last');
    url.searchParams.set('section_id', SECTION_ID);

    let doc = this.#cache.get(url.toString());
    if (!doc) {
      const controller = new AbortController();
      this.#controller = controller;
      try {
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error(`predictive search ${response.status}`);
        doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      } catch (error) {
        if (/** @type {Error} */ (error).name !== 'AbortError') console.warn('[lc-search]', error);
        return;
      }
      if (controller.signal.aborted) return;
      this.#cache.set(url.toString(), doc);
    }
    if (this.refs.input.value.trim() !== query) return;
    this.#render(doc, query, products, queries);
  }

  /**
   * @param {Document} doc
   * @param {string} query
   * @param {number} maxProducts
   * @param {number} maxQueries
   */
  #render(doc, query, maxProducts, maxQueries) {
    const productItems = [...doc.querySelectorAll('[data-lc-search="products"] > li')].slice(0, maxProducts);
    const shopifyItems = [...doc.querySelectorAll('[data-lc-search="queries"] > li')];
    const taken = new Set(shopifyItems.map((li) => normalize(li.textContent ?? '')));
    // the merchant's order is kept; what was typed itself is never suggested back
    const ownItems = this.#popular
      .filter(({ text }) => matches(text, query) && !taken.has(normalize(text)) && normalize(text) !== normalize(query))
      .map((entry) => this.#popularItem(entry, query));
    const queryItems = [...shopifyItems, ...ownItems].slice(0, maxQueries);

    this.refs.products.replaceChildren(...productItems.map((li) => document.importNode(li, true)));
    this.refs.queries.replaceChildren(...queryItems.map((li) => (li.ownerDocument === document ? li : document.importNode(li, true))));
    this.refs.popular.hidden = queryItems.length === 0;
    this.refs.products.hidden = productItems.length === 0;

    const nothing = productItems.length === 0 && queryItems.length === 0;
    this.refs.empty.hidden = !nothing;
    if (nothing) this.refs.empty.textContent = (this.dataset.emptyText || '').replace('[query]', `“${query}”`);

    const all = new URL(this.dataset.searchUrl || '/search', location.origin);
    all.searchParams.set('q', query);
    all.searchParams.set('options[prefix]', 'last');
    all.searchParams.set('type', 'product');
    /** @type {HTMLAnchorElement} */ (this.refs.all).href = all.pathname + all.search;
    this.refs.all.hidden = productItems.length === 0;

    this.refs.results.hidden = false;
  }
}

if (!customElements.get('lc-search')) customElements.define('lc-search', LcSearch);
